import {Alert, Attributes, AttributesRow} from 'platform/components';
import {GridItem, Text, Grid, Space, Box, Hide, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {useField} from 'react-final-form';
import {useSelector} from 'react-redux';

import {isNil, isNotEmpty} from 'ramda';
import {isNotNil, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {Nullish, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {selectAudit, selectTestDrive} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {AuditParamDefinition} from '../../../types/AuditParamDefinition';
import {TConditionFormValue} from '../../../types/ConditionTypes';
import {getNameAccordingToLocale} from '../../../utils/getNameAccordingToLocale';
import {AuditParamType} from '../types/AuditParamType';
import {findByParamType} from '../utils/findByParamType';
import {getFormFieldName} from '../utils/getFormFieldName';
import {AuditFormField} from './AuditFormField';
import {CarAuditAppBanner} from './CarAuditAppBanner';
import {CategoryGeneralComment} from './CategoryGeneralComment';
import {TestDriveRouteMap} from './TestDriveRouteMap/TestDriveRouteMap';

const AlertWrapper = styled.div`
  margin-bottom: 16px;
`;

const timeParams: string[] = [AuditParamType.START_TIME, AuditParamType.END_TIME];

export const TestDrive = (props: TestIdProps) => {
  const locale = useSelector(selectUserSelectedLanguage);
  const testDriveStructure = useSelector(selectTestDrive);
  const audit = useSelector(selectAudit);
  const formatDateTime = useDateTimeFormatter();

  const commentParamDefinition = findByParamType(
    testDriveStructure?.paramDefinitions?.additionalInformation,
    AuditParamType.ADDITIONAL_COMMENT
  );

  const rowsParam: AttributesRow[] = [];

  let dateValue;
  let isGpsSignalWeak = false;
  let encodedRoute: string | Nullish;
  let isNotTested = true;

  const getRowValue = (paramDefinition: AuditParamDefinition, fieldValue?: TConditionFormValue) => {
    // Handle toggle/boolean fields
    if (paramDefinition.type === AuditParamType.TOGGLE) {
      if (fieldValue === null || fieldValue === undefined) {
        return EMPTY_PLACEHOLDER;
      }
      return fieldValue ? i18n.t('general.labels.yes') : i18n.t('general.labels.no');
    }

    if (!fieldValue) {
      return EMPTY_PLACEHOLDER;
    }

    if (Boolean(fieldValue) && timeParams.includes(paramDefinition.type)) {
      return formatDateTime('timeShort', parseDate(String(fieldValue)));
    }

    if (paramDefinition.unit) {
      return `${fieldValue} ${paramDefinition.unit}`;
    }

    return String(fieldValue);
  };

  const toggleParamDefinition = findByParamType(
    testDriveStructure?.paramDefinitions?.relatedActions,
    AuditParamType.TOGGLE
  );

  // Inspections on a structure version older than the route param have no place for the route.
  const routeParamDefinition = findByParamType(
    testDriveStructure?.paramDefinitions?.relatedActions,
    AuditParamType.TEST_DRIVE_ROUTE
  );

  testDriveStructure?.paramDefinitions?.relatedActions?.forEach((paramDefinition) => {
    const fieldName = getFormFieldName(testDriveStructure.id, paramDefinition.id);
    const fieldValue = audit?.fields?.[fieldName];

    match(paramDefinition.type)
      .with(AuditParamType.CATEGORY_STATE, AuditParamType.TOGGLE, () => undefined)
      .with(AuditParamType.TEST_DRIVE_DATE, () => {
        if (Boolean(fieldValue) && fieldValue !== '') {
          dateValue = formatDateTime('dateShort', parseDate(String(fieldValue)));
        }
      })
      .with(AuditParamType.WEAK_GPS_SIGNAL, () => {
        isGpsSignalWeak = Boolean(fieldValue);
      })
      .with(AuditParamType.TEST_DRIVE_ROUTE, () => {
        encodedRoute = isString(fieldValue) ? fieldValue : undefined;
      })
      .otherwise(() => {
        if (Boolean(fieldValue) && isNotTested) {
          isNotTested = false;
        }

        rowsParam.push({
          label: paramDefinition.label
            ? getNameAccordingToLocale(locale, paramDefinition.label)
            : '',
          value: getRowValue(paramDefinition, fieldValue),
        });
      });
  });

  const getParamText = (type: AuditParamType) => {
    const paramDefinition = findByParamType(
      testDriveStructure?.paramDefinitions?.relatedActions,
      type
    );
    if (isNil(paramDefinition) || isNil(testDriveStructure)) {
      return null;
    }
    const fieldValue = audit?.fields?.[getFormFieldName(testDriveStructure.id, paramDefinition.id)];
    return isNotNilOrEmpty(fieldValue) ? getRowValue(paramDefinition, fieldValue) : null;
  };

  const routeSummaryParts = [
    getParamText(AuditParamType.MILEAGE),
    getParamText(AuditParamType.TIME_DURATION),
  ].filter(isNotNil);
  const routeSummary = isNotEmpty(routeSummaryParts) ? routeSummaryParts.join(' · ') : null;

  // A drive recorded in the app needs no app advertisement, so its route takes the banner's place.
  const isRouteShownInsteadOfBanner = isNotNil(routeParamDefinition) && !isNotTested;

  const toggleFieldName = toggleParamDefinition
    ? getFormFieldName(testDriveStructure?.id ?? '', toggleParamDefinition.id)
    : undefined;

  const toggleField = useField(toggleFieldName ?? '', {subscription: {value: true}});
  const isTestDrivePerformedToggled = Boolean(toggleField.input.value);

  return (
    <>
      <Show when={isGpsSignalWeak}>
        <AlertWrapper data-testid={suffixTestId('alertWrapper', props)}>
          <Alert
            data-testid={suffixTestId('warning', props)}
            type="inline"
            variant="warning"
            title={i18n.t('entity.condition.notifications.finishedWithWarnings')}
            message={i18n.t('entity.condition.notifications.lowGpsSignal')}
          />
        </AlertWrapper>
      </Show>
      <Show
        when={
          toggleParamDefinition && testDriveStructure
            ? {toggleParamDefinition, testDriveStructure}
            : null
        }
      >
        {(testDrive) => (
          <Box width="fit-content">
            <AuditFormField
              paramDefinition={testDrive.toggleParamDefinition}
              categoryId={testDrive.testDriveStructure.id}
              uniqueKey={testDrive.testDriveStructure.uniqueKey}
              data-testid={suffixTestId('testDrivePerformedToggle', props)}
            />
            <Space vertical={4} />
          </Box>
        )}
      </Show>
      <Grid columns={2}>
        <GridItem>
          <Hide when={isTestDrivePerformedToggled}>
            {isNotTested ? (
              <Text
                alternative
                color="danger"
                data-testid={suffixTestId('vehicleNotTested', props)}
              >
                {i18n.t('entity.condition.labels.vehicleNotTested')}
              </Text>
            ) : (
              <Text alternative data-testid={suffixTestId('testDate', props)}>
                {dateValue}
              </Text>
            )}
          </Hide>
          <Space vertical={2} />
          <Attributes rows={rowsParam} data-testid={props['data-testid']} />
        </GridItem>
        <GridItem>
          <Show when={isRouteShownInsteadOfBanner}>
            <TestDriveRouteMap
              encodedRoute={encodedRoute}
              isTestDriveRecorded={!isNotTested}
              summary={routeSummary}
              fallback={<CarAuditAppBanner />}
              data-testid={suffixTestId('route', props)}
            />
          </Show>
          <Hide when={isRouteShownInsteadOfBanner}>
            <CarAuditAppBanner />
          </Hide>
        </GridItem>
      </Grid>
      <CategoryGeneralComment
        category={testDriveStructure}
        paramDefinitionId={commentParamDefinition?.id}
      />
    </>
  );
};
